import React, { useMemo } from 'react';
import { Alert, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { Button } from '~components';
import Icon, { faExclamationTriangle } from '~components/icon';

import { ClientInvoice } from '~support-at-home/models';

interface ComponentProps {
  invoices: ClientInvoice[];
  targetStatusLabel: string;
  isOpen: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirm: (eligibleIds: string[]) => Promise<void> | void;
}

// TAV-2053 bulk-status confirmation. Surfaces the eligibility split before
// firing the bulk mutation: GoCardless-locked rows are shown as skipped, and
// the confirm button is disabled when no rows are eligible. Filters the
// payload client-side so the API isn't asked to update locked rows it would
// only refuse anyway — clearer UX, lighter payload.
const EligibilityConfirmDialog: React.FC<ComponentProps> = ({
  invoices,
  targetStatusLabel,
  isOpen,
  isSubmitting = false,
  onClose,
  onConfirm,
}) => {
  const { eligibleIds, lockedCount } = useMemo(() => {
    const eligible: string[] = [];
    let locked = 0;
    for (const invoice of invoices) {
      if (invoice.payment_source === 'gocardless') {
        locked += 1;
      } else {
        eligible.push(invoice.id);
      }
    }
    return { eligibleIds: eligible, lockedCount: locked };
  }, [invoices]);

  const eligibleCount = eligibleIds.length;
  const canConfirm = eligibleCount > 0 && !isSubmitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    await onConfirm(eligibleIds);
  };

  // While the bulk mutation is in flight, lock the modal closed: backdrop
  // clicks, Esc, and the header X all become no-ops. Cancel was already
  // disabled. Without this, the user could close mid-flight and retry,
  // doubling up the request.
  const dismiss = isSubmitting ? undefined : onClose;

  return (
    <Modal
      isOpen={isOpen}
      toggle={dismiss}
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
      data-cy="invoice-status-confirm-dialog"
      aria-labelledby="invoice-status-confirm-header"
      labelledBy="invoice-status-confirm-header"
    >
      <ModalHeader toggle={dismiss} id="invoice-status-confirm-header">
        Mark as {targetStatusLabel}
      </ModalHeader>
      <ModalBody>
        <p data-cy="invoice-status-confirm-eligible">
          <strong>{eligibleCount}</strong>{' '}
          {eligibleCount === 1 ? 'invoice' : 'invoices'} will be marked as {targetStatusLabel}.
        </p>
        {lockedCount > 0 && (
          <Alert color="warning" className="mb-3">
            <Icon icon={faExclamationTriangle} className="me-2" />
            <span data-cy="invoice-status-confirm-skipped">
              <strong>{lockedCount}</strong>{' '}
              {lockedCount === 1 ? 'invoice' : 'invoices'} will be skipped — managed by GoCardless and cannot be
              manually changed.
            </span>
          </Alert>
        )}
        {eligibleCount === 0 && (
          <p className="text-muted mb-0">No eligible invoices in the current selection.</p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={!canConfirm}
          data-cy="invoice-status-confirm-button"
        >
          {isSubmitting ? 'Updating…' : `Mark ${eligibleCount} as ${targetStatusLabel}`}
        </Button>
        <Button color="secondary" onClick={onClose} disabled={isSubmitting} data-cy="invoice-status-cancel-button">
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default EligibilityConfirmDialog;
